import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import { cn } from 'cn';
import dlv from 'dlv';
import { Check } from 'lucide-react';
import { Controller, ControllerProps, FieldPath, FieldValues, FormProvider, useFormContext, useWatch } from 'react-hook-form';
import { type FormItemVariant, FormItemVariantContext } from '@/components/ui/form-item-variant-context';
import { Label } from '@/components/ui/label';
const Form = FormProvider;
type FormFieldContextValue<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
  name: TName;
};
const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);
const FormField = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  const formFieldContextValue = React.useMemo(() => ({
    name: props.name
  }), [props.name]);
  return <FormFieldContext.Provider value={formFieldContextValue} data-sentry-element="FormFieldContext.Provider" data-sentry-component="FormField" data-sentry-source-file="form.tsx">
      <Controller {...props} data-sentry-element="Controller" data-sentry-source-file="form.tsx" />
    </FormFieldContext.Provider>;
};
const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const {
    getFieldState,
    formState
  } = useFormContext();
  const fieldState = getFieldState(fieldContext.name, formState);
  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }
  const {
    id
  } = itemContext;

  // Ensure errors are returned even for untouched fields in onChange mode
  // getFieldState might not return errors for untouched fields, so we check formState.errors directly
  // Use dlv to safely access nested errors (e.g., "address.street1" -> formState.errors.address?.street1)
  const error = fieldState.error ?? dlv(formState.errors, fieldContext.name) ?? undefined;
  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
    error
  };
};

/**
 * `field` is the web-form look (FormField in the form builder): the row
 * tints with the brand on focus (red when invalid), the label darkens as
 * you type, and a green check marks a filled, valid text field.
 */
type FormItemContextValue = {
  id: string;
  variant?: FormItemVariant;
};
const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);
const FieldFormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({
  className,
  children,
  ...props
}, ref) => {
  const {
    name
  } = React.useContext(FormFieldContext);
  const {
    control,
    getFieldState,
    formState
  } = useFormContext();
  const value = useWatch({
    control,
    name
  });
  const fieldState = getFieldState(name, formState);
  const error = fieldState.error ?? dlv(formState.errors, name);
  // Only text-like values earn the check; toggles and pickers carry
  // their own state. An edited value waits until the field is left (and
  // so validated) — a saved, untouched value is already known good.
  const isComplete = typeof value === 'string' && value.trim().length > 0 && !error && (!fieldState.isDirty || fieldState.isTouched);
  return <div ref={ref} data-complete={isComplete || undefined} data-invalid={error ? true : undefined} className={cn(
  // Negative margin keeps the tinted padding out of the layout.
  'group/form-item relative -m-1 space-y-1.5 rounded-lg p-1 transition-colors duration-150 md:-m-2 md:p-2', 'focus-within:bg-primary-150 dark:focus-within:bg-primary/15 data-[invalid]:focus-within:bg-destructive/10', className)} {...props}>
      {children}
      {isComplete ? <Check aria-hidden strokeWidth={2.5} className={cn('pointer-events-none absolute top-1.5 right-1 size-4 text-green-500 md:top-2.5 md:right-2',
    // While a changed value is being edited it hasn't been
    // re-validated yet, so hold the check until focus leaves.
    fieldState.isDirty ? 'group-focus-within/form-item:hidden' : 'group-focus-within/form-item:text-muted-foreground')} /> : null}
    </div>;
});
FieldFormItem.displayName = 'FieldFormItem';
interface FormItemProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: FormItemVariant;
}
const FormItem = React.forwardRef<HTMLDivElement, FormItemProps>(({
  className,
  variant: requestedVariant = 'default',
  ...props
}, ref) => {
  const id = React.useId();
  // The field look ships with refreshed shell; off, every item is default.
  const variant = requestedVariant;
  const formItemContextValue = React.useMemo(() => ({
    id,
    variant
  }), [id, variant]);
  return <FormItemContext.Provider value={formItemContextValue}>
        <FormItemVariantContext.Provider value={variant}>
          {variant === 'field' ? <FieldFormItem ref={ref} className={className} {...props} /> : <div ref={ref} className={cn('refresh:space-y-1.5 space-y-2', className)} {...props} />}
        </FormItemVariantContext.Provider>
      </FormItemContext.Provider>;
});
FormItem.displayName = 'FormItem';
const FormLabel = React.forwardRef<React.ElementRef<typeof LabelPrimitive.Root>, React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>>(({
  className,
  ...props
}, ref) => {
  const {
    error,
    formItemId
  } = useFormField();
  const {
    variant
  } = React.useContext(FormItemContext);
  return <Label ref={ref} className={cn(variant === 'field' ? 'group-focus-within/form-item:text-foreground group-data-[complete]/form-item:text-foreground group-data-[invalid]/form-item:text-foreground pr-6' : {
    'text-destructive': error
  }, className)} htmlFor={formItemId} {...props} />;
});
FormLabel.displayName = 'FormLabel';
const FormControl = React.forwardRef<React.ElementRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot>>(({
  ...props
}, ref) => {
  const {
    error,
    formItemId,
    formDescriptionId,
    formMessageId
  } = useFormField();
  return <Slot ref={ref} id={formItemId} aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`} aria-invalid={!!error} {...props} />;
});
FormControl.displayName = 'FormControl';
const FormDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({
  className,
  ...props
}, ref) => {
  const {
    formDescriptionId
  } = useFormField();
  return <p ref={ref} id={formDescriptionId} className={cn('text-muted-foreground refresh:text-xs refresh:leading-4 text-sm', className)} {...props} />;
});
FormDescription.displayName = 'FormDescription';
interface FormMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {
  /** When true, does not reserve min-height (no layout shift when message appears) */
  noMinHeight?: boolean;
}
const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(({
  className,
  children,
  noMinHeight,
  ...props
}, ref) => {
  const {
    error,
    formMessageId
  } = useFormField();
  const {
    variant
  } = React.useContext(FormItemContext);
  const body = error ? String(error?.message) : children;

  // A field row's tint hugs the input, so no empty reserved line.
  if ((noMinHeight || variant === 'field') && !body) {
    return null;
  }
  return <p ref={ref} id={formMessageId} className={cn('refresh:text-xs refresh:leading-4 text-sm font-medium',
  // Reserve space for one line of message text.
  {
    'refresh:min-h-4 min-h-5': !noMinHeight
  }, body ? 'text-destructive' : 'text-transparent',
  // Invisible when no error
  className)} {...props}>
        {body || (!noMinHeight ? '\u00A0' : null)}
        {/* Non-breaking space to maintain height when min-height is used */}
      </p>;
});
FormMessage.displayName = 'FormMessage';
export { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField };